import { IBlock } from '@core/typings';
import { EMAIL_BLOCK_CLASS_NAME } from '@core/constants';

import { isString } from 'lodash';

import { classnames } from '@core/utils/classnames';
import { getNodeIdxClassName, getNodeTypeClassName } from '@core/utils';
import { resolveColorTagValue, isMergeTagExpression } from '@core/utils/resolveColorTagValue';
import { normalizeLinkHref } from '@core/utils/normalizeLinkHref';

// Attributes whose value can be a dynamic brand-colour merge tag. Matches both
// kebab-case keys ("color", "border-color", "container-background-color") and
// camelCase keys ("cellBorderColor", "cellBackgroundColor") so every colour
// attribute resolves regardless of naming convention.
const COLOR_ATTRIBUTE_PATTERN = /(^|-|[a-z])[Cc]olor$/;

export function getAdapterAttributesString(params: Parameters<IBlock['render']>[0]) {
  const { data, idx } = params;
  const isTest = params.mode === 'testing';
  const attributes = { ...data.attributes };
  const keepClassName = isTest ? params.keepClassName : false;

  // In the editor preview, resolve merge-tag colour values (e.g. brand colours like
  // "{{user.brandPrimaryColor}}") to their real hex so the canvas shows the colour.
  // The saved model and the production output keep the tag (mode !== 'testing').
  if (isTest && (params as any).dataSource) {
    Object.keys(attributes).forEach(key => {
      const val = attributes[key as keyof typeof attributes];
      if (
        typeof val === 'string' &&
        COLOR_ATTRIBUTE_PATTERN.test(key) &&
        isMergeTagExpression(val)
      ) {
        const resolved = resolveColorTagValue(val, (params as any).dataSource);
        if (resolved) {
          (attributes as any)[key] = resolved;
        }
      }
    });
  }

  if (isTest && idx) {
    attributes['css-class'] = classnames(
      attributes['css-class'],
      EMAIL_BLOCK_CLASS_NAME,
      getNodeIdxClassName(idx),
      getNodeTypeClassName(data.type),
      (data as any)?.data?.hidden ? 'is-hidden' : undefined,
    );
  }

  if (keepClassName) {
    attributes['css-class'] = classnames(
      attributes['css-class'],
      getNodeTypeClassName(data.type),
    );
  }

  if (isString(attributes.href)) {
    attributes.href = normalizeLinkHref(attributes.href);
  }

  let attributeStr = '';
  for (let key in attributes) {
    const keyName = key as keyof typeof attributes;
    const val = attributes[keyName];
    if (isString(val) && val) {
      const splitter = ' ';
      attributeStr += `${key}="${val.replace(/"/gm, '')}"` + splitter;
    }
  }

  return attributeStr;
}
